import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';

const CartContext = createContext(null);

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within CartProvider');
    }
    return context;
};

const getCartKey = () => {
    try {
        const user = JSON.parse(localStorage.getItem('prixdeal_user') || '{}');
        return user.id ? `prixdeal_cart_${user.id}` : null;
    } catch (err) {
        console.error('CartContext: failed to parse user from localStorage', err);
        return null;
    }
};

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState([]);
    const [isOrderCartOpen, setIsOrderCartOpen] = useState(false);
    const [isQuoteCartOpen, setIsQuoteCartOpen] = useState(false);
    const [sourceOfferId, setSourceOfferId] = useState(() => localStorage.getItem('prixdeal_offer_id') || null);

    const loadCart = useCallback(() => {
        const key = getCartKey();
        if (!key) {
            setItems([]);
            return;
        }
        try {
            const savedCart = localStorage.getItem(key);
            if (savedCart) {
                const parsed = JSON.parse(savedCart);
                if (Array.isArray(parsed)) {
                    const migrated = parsed.map(item => ({
                        ...item,
                        cart_type: item.cart_type || (item.ready_to_order ? 'order' : 'quote'),
                    }));
                    setItems(migrated);
                    return;
                }
            }
        } catch (err) {
            console.error('CartContext: failed to load cart', err);
        }
        setItems([]);
    }, []);

    // Load cart on mount
    useEffect(() => {
        loadCart();
    }, [loadCart]);

    // Listen for auth changes (login/logout)
    useEffect(() => {
        const handleStorage = (e) => {
            if (e.key === 'prixdeal_user' || e.key === 'prixdeal_token') {
                loadCart();
            }
        };
        window.addEventListener('storage', handleStorage);

        // Also listen for custom auth event
        const handleAuth = () => loadCart();
        window.addEventListener('auth-changed', handleAuth);

        return () => {
            window.removeEventListener('storage', handleStorage);
            window.removeEventListener('auth-changed', handleAuth);
        };
    }, [loadCart]);

    // Save cart whenever items change
    useEffect(() => {
        const key = getCartKey();
        if (key) {
            localStorage.setItem(key, JSON.stringify(items));
        }
        // Clean up old shared key
        localStorage.removeItem('prixdeal_cart');
    }, [items]);

    const addItem = useCallback((product, quantity = 1) => {
        const cartKey = getCartKey();
        if (!cartKey) {
            toast.error('Connectez-vous pour commander ce produit');
            return;
        }

        const isOrder = product.ready_to_order === true;
        const cartType = isOrder ? 'order' : 'quote';

        setItems(prev => {
            const existing = prev.find(item => item.product_id === product.id);
            if (existing) {
                return prev.map(item =>
                    item.product_id === product.id
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            }
            return [...prev, {
                product_id: product.id,
                product_name: product.name,
                price: product.price,
                unit: product.unit,
                image_url: product.image_url,
                quantity,
                min_order: product.min_order,
                package_weight: product.contenance || product.package_weight || 1,
                cart_type: cartType,
            }];
        });
    }, []);

    const removeItem = useCallback((productId) => {
        setItems(prev => prev.filter(item => item.product_id !== productId));
    }, []);

    const updateQuantity = useCallback((productId, quantity) => {
        if (quantity <= 0) {
            removeItem(productId);
            return;
        }
        setItems(prev =>
            prev.map(item =>
                item.product_id === productId ? { ...item, quantity } : item
            )
        );
    }, [removeItem]);

    const clearCart = useCallback(() => {
        setItems([]);
        setSourceOfferId(null);
        localStorage.removeItem('prixdeal_offer_id');
    }, []);

    const clearOrderItems = useCallback(() => {
        setItems(prev => prev.filter(item => item.cart_type !== 'order'));
        setSourceOfferId(null);
        localStorage.removeItem('prixdeal_offer_id');
    }, []);

    const clearQuoteItems = useCallback(() => {
        setItems(prev => prev.filter(item => item.cart_type !== 'quote'));
    }, []);

    const orderItems = items.filter(i => i.cart_type === 'order');
    const quoteItems = items.filter(i => i.cart_type === 'quote');

    const calcItemTotal = useCallback((item) => {
        const contenance = item.package_weight > 0 ? item.package_weight : 1;
        return item.quantity * contenance * item.price;
    }, []);

    const orderTotal = orderItems.reduce((sum, item) => sum + calcItemTotal(item), 0);
    const orderCount = orderItems.reduce((sum, item) => sum + item.quantity, 0);
    const quoteCount = quoteItems.reduce((sum, item) => sum + item.quantity, 0);

    const openOrderCart = useCallback(() => { setIsQuoteCartOpen(false); setIsOrderCartOpen(true); }, []);
    const openQuoteCart = useCallback(() => { setIsOrderCartOpen(false); setIsQuoteCartOpen(true); }, []);
    const closeOrderCart = useCallback(() => setIsOrderCartOpen(false), []);
    const closeQuoteCart = useCallback(() => setIsQuoteCartOpen(false), []);

    const setOfferIdFn = useCallback((offerId) => {
        setSourceOfferId(offerId);
        if (offerId) {
            localStorage.setItem('prixdeal_offer_id', offerId);
        } else {
            localStorage.removeItem('prixdeal_offer_id');
        }
    }, []);

    const value = useMemo(() => ({
        items, orderItems, quoteItems,
        addItem, updateQuantity, removeItem,
        clearCart, clearOrderItems, clearQuoteItems,
        orderTotal, orderCount, quoteCount, calcItemTotal,
        isOrderCartOpen, isQuoteCartOpen,
        openOrderCart, openQuoteCart, closeOrderCart, closeQuoteCart,
        sourceOfferId, setOfferId: setOfferIdFn, loadCart,
    }), [items, orderItems, quoteItems, orderTotal, orderCount, quoteCount,
        isOrderCartOpen, isQuoteCartOpen, sourceOfferId,
        addItem, updateQuantity, removeItem, clearCart, clearOrderItems,
        clearQuoteItems, calcItemTotal, openOrderCart, openQuoteCart,
        closeOrderCart, closeQuoteCart, setOfferIdFn, loadCart]);

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};
